import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useHistory } from 'react-router-dom'

import {
  getSafetyReportJobStatus,
  SafetyReportJobStatusResponse,
} from '../api/projectDocuments'
import { useGeneratedPaths } from '../hooks/useGeneratedPaths'

import { useNotifications } from './notificationProvider'
import { useUserContext } from './userContext'

const STORAGE_KEY = 'nextera_safety_report_jobs_v1'

/** Polling: faster at first, slower while jobs stay pending/running (reduces request spam). */
const POLL_MS_TIER_SHORT = 4000
const POLL_MS_TIER_MID = 10000
const POLL_MS_TIER_LONG = 20000
const POLL_AGE_TIER1_MS = 60_000
const POLL_AGE_TIER2_MS = 300_000
const POLL_MIN_INTERVAL_MS = 4000
const POLL_ERROR_BACKOFF_MS = 30000
const POLL_ERROR_MAX_CONSECUTIVE = 3

/** Client watchdog: after this (aligned with Celery soft limit + buffer), drop job + notify. Not shown as elapsed time. */
const CLIENT_MAX_JOB_WAIT_MS = 20 * 60 * 1000

export type PendingSafetyReportJob = {
  jobId: string
  projectPublicId: string
  projectName: string
}

export type SafetyReportJobPollStatus =
  | SafetyReportJobStatusResponse['status']
  | null

type SafetyReportJobsContextValue = {
  pendingJobs: PendingSafetyReportJob[]
  registerPendingJob: (job: PendingSafetyReportJob) => void
  /** Last known API status per job (pending/running/failed/succeeded) for subtle UI copy. No timestamps. */
  jobStatusById: Record<string, SafetyReportJobPollStatus>
}

const SafetyReportJobsContext = createContext<
  SafetyReportJobsContextValue | undefined
>(undefined)

function loadStoredJobs(): PendingSafetyReportJob[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter((j): j is PendingSafetyReportJob =>
      Boolean(
        j &&
          typeof j === 'object' &&
          typeof (j as PendingSafetyReportJob).jobId === 'string' &&
          typeof (j as PendingSafetyReportJob).projectPublicId === 'string',
      ),
    )
  } catch {
    return []
  }
}

function pollDelayForOldestJobMs(
  jobs: PendingSafetyReportJob[],
  registeredAt: Map<string, number>,
): number {
  if (!jobs.length) return POLL_MS_TIER_SHORT
  const now = Date.now()
  let oldest = now
  for (const j of jobs) {
    const t = registeredAt.get(j.jobId) ?? now
    if (t < oldest) oldest = t
  }
  const age = now - oldest
  if (age < POLL_AGE_TIER1_MS) return POLL_MS_TIER_SHORT
  if (age < POLL_AGE_TIER2_MS) return POLL_MS_TIER_MID
  return POLL_MS_TIER_LONG
}

export const SafetyReportJobsProvider: React.FC = ({ children }) => {
  const [pendingJobs, setPendingJobs] = useState<PendingSafetyReportJob[]>([])
  const [jobStatusById, setJobStatusById] = useState<
    Record<string, SafetyReportJobPollStatus>
  >({})
  const pendingRef = useRef(pendingJobs)
  pendingRef.current = pendingJobs

  const jobRegisteredAtRef = useRef<Map<string, number>>(new Map())
  const notifiedRef = useRef<Set<string>>(new Set())
  const watchdogNotifiedRef = useRef<Set<string>>(new Set())
  const consecutiveFailuresRef = useRef<Map<string, number>>(new Map())
  const tickInFlightRef = useRef(false)
  const timeoutRef = useRef<number | null>(null)
  const nextAllowedPollAtRef = useRef(0)

  const { state: userState } = useUserContext()
  const { addNotification } = useNotifications()
  const history = useHistory()
  const { generateProjectReportsPath } = useGeneratedPaths()
  const addNotificationRef = useRef(addNotification)
  const historyRef = useRef(history)
  const generateProjectReportsPathRef = useRef(generateProjectReportsPath)
  addNotificationRef.current = addNotification
  historyRef.current = history
  generateProjectReportsPathRef.current = generateProjectReportsPath

  const stampJobsIfNeeded = useCallback((jobs: PendingSafetyReportJob[]) => {
    const m = jobRegisteredAtRef.current
    const now = Date.now()
    for (const j of jobs) {
      if (!m.has(j.jobId)) m.set(j.jobId, now)
    }
  }, [])

  useEffect(() => {
    if (!userState.isAuthenticated) {
      setPendingJobs([])
      setJobStatusById({})
      jobRegisteredAtRef.current.clear()
      watchdogNotifiedRef.current.clear()
      return
    }
    const loaded = loadStoredJobs()
    stampJobsIfNeeded(loaded)
    setPendingJobs(loaded)
  }, [userState.isAuthenticated, stampJobsIfNeeded])

  useEffect(() => {
    if (!userState.isAuthenticated || userState.user_type !== 'super') {
      return
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pendingJobs))
    } catch {
      // ignore quota / private mode
    }
  }, [pendingJobs, userState.isAuthenticated, userState.user_type])

  const removeJob = useCallback((jobId: string) => {
    jobRegisteredAtRef.current.delete(jobId)
    watchdogNotifiedRef.current.delete(jobId)
    consecutiveFailuresRef.current.delete(jobId)
    setJobStatusById(prev => {
      if (!(jobId in prev)) return prev
      const next = { ...prev }
      delete next[jobId]
      return next
    })
    setPendingJobs(prev => prev.filter(j => j.jobId !== jobId))
  }, [])

  const registerPendingJob = useCallback((job: PendingSafetyReportJob) => {
    notifiedRef.current.delete(job.jobId)
    if (!jobRegisteredAtRef.current.has(job.jobId)) {
      jobRegisteredAtRef.current.set(job.jobId, Date.now())
    }
    setPendingJobs(prev => {
      if (prev.some(p => p.jobId === job.jobId)) return prev
      return [...prev, job]
    })
  }, [])

  useEffect(() => {
    if (!userState.isAuthenticated || userState.user_type !== 'super') {
      if (timeoutRef.current != null) {
        window.clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      return undefined
    }
    if (!pendingJobs.length) {
      if (timeoutRef.current != null) {
        window.clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      return undefined
    }

    const scheduleNext = (delayMs: number) => {
      const now = Date.now()
      const boundedDelay = Math.max(
        POLL_MIN_INTERVAL_MS,
        delayMs,
        nextAllowedPollAtRef.current - now,
      )
      if (timeoutRef.current != null) {
        window.clearTimeout(timeoutRef.current)
      }
      if (process.env.NODE_ENV !== 'production') {
        // Debug only: confirm scheduler cadence and avoid accidental poll bursts.
        // eslint-disable-next-line no-console
        console.debug('safety_report_poll_next', {
          delay_ms: boundedDelay,
          pending_count: pendingRef.current.length,
        })
      }
      timeoutRef.current = window.setTimeout(() => void runTick(), boundedDelay)
    }

    const runTick = async () => {
      if (!pendingRef.current.length) return
      if (tickInFlightRef.current) {
        scheduleNext(POLL_MS_TIER_SHORT)
        return
      }
      tickInFlightRef.current = true
      nextAllowedPollAtRef.current = Date.now() + POLL_MIN_INTERVAL_MS
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('safety_report_tick_start', {
          pending_count: pendingRef.current.length,
        })
      }
      try {
        const snapshot = [...pendingRef.current]
        if (!snapshot.length) return

        const now = Date.now()
        const toPoll: PendingSafetyReportJob[] = []
        for (const job of snapshot) {
          const started = jobRegisteredAtRef.current.get(job.jobId) ?? now
          if (
            now - started > CLIENT_MAX_JOB_WAIT_MS &&
            !watchdogNotifiedRef.current.has(job.jobId)
          ) {
            watchdogNotifiedRef.current.add(job.jobId)
            removeJob(job.jobId)
            addNotificationRef.current(
              `Safety report for ${job.projectName} is taking longer than expected. Open Project reports to see if it finished.`,
              'warning',
            )
            continue
          }
          toPoll.push(job)
        }

        if (!toPoll.length) return

        const results = await Promise.all(
          toPoll.map(async job => {
            try {
              const res = await getSafetyReportJobStatus(
                job.projectPublicId,
                job.jobId,
              )
              return {
                job,
                res: res as SafetyReportJobStatusResponse,
                err: null,
              }
            } catch {
              return { job, res: null, err: true as const }
            }
          }),
        )

        setJobStatusById(prev => {
          const next = { ...prev }
          for (const { job, res } of results) {
            if (res) next[job.jobId] = res.status
          }
          return next
        })

        for (const { job, res, err } of results) {
          if (err) {
            const failures =
              (consecutiveFailuresRef.current.get(job.jobId) || 0) + 1
            consecutiveFailuresRef.current.set(job.jobId, failures)
            if (failures >= POLL_ERROR_MAX_CONSECUTIVE) {
              removeJob(job.jobId)
              addNotificationRef.current(
                `Lost track of safety report job for ${job.projectName}. Check Reports for a new document or try again.`,
                'warning',
              )
            }
            continue
          }
          if (!res) continue
          consecutiveFailuresRef.current.set(job.jobId, 0)

          if (res.status === 'succeeded' && res.document) {
            if (notifiedRef.current.has(job.jobId)) continue
            notifiedRef.current.add(job.jobId)
            removeJob(job.jobId)
            addNotificationRef.current(
              `Safety report ready for ${job.projectName}`,
              'success',
              {
                actionLabel: 'Open report',
                onAction: () => {
                  historyRef.current.push(
                    `${generateProjectReportsPathRef.current(job.projectPublicId)}?documentId=${res.document!.id}`,
                  )
                },
              },
            )
          } else if (res.status === 'succeeded' && !res.document) {
            if (notifiedRef.current.has(`${job.jobId}_fail`)) continue
            notifiedRef.current.add(`${job.jobId}_fail`)
            removeJob(job.jobId)
            addNotificationRef.current(
              `Safety report for ${job.projectName} could not be opened. Check Project reports or try again.`,
              'error',
            )
          } else if (res.status === 'failed') {
            if (notifiedRef.current.has(`${job.jobId}_fail`)) continue
            notifiedRef.current.add(`${job.jobId}_fail`)
            removeJob(job.jobId)
            addNotificationRef.current(
              `Safety report failed for ${job.projectName}: ${res.error || 'Unknown error'}`,
              'error',
            )
          }
        }
      } finally {
        tickInFlightRef.current = false
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('safety_report_tick_end', {
            pending_count: pendingRef.current.length,
          })
        }
        const nextSnapshot = pendingRef.current
        if (nextSnapshot.length) {
          let delay = pollDelayForOldestJobMs(
            nextSnapshot,
            jobRegisteredAtRef.current,
          )
          const hasRecentErrors = nextSnapshot.some(
            j => (consecutiveFailuresRef.current.get(j.jobId) || 0) > 0,
          )
          if (hasRecentErrors) {
            delay = Math.max(delay, POLL_ERROR_BACKOFF_MS)
          }
          scheduleNext(delay)
        }
      }
    }

    if (pendingRef.current.length) {
      stampJobsIfNeeded(pendingRef.current)
      scheduleNext(POLL_MIN_INTERVAL_MS)
    }

    return () => {
      if (timeoutRef.current != null) {
        window.clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [
    userState.isAuthenticated,
    userState.user_type,
    removeJob,
    pendingJobs.length,
    stampJobsIfNeeded,
  ])

  const value = useMemo(
    () => ({ pendingJobs, registerPendingJob, jobStatusById }),
    [pendingJobs, registerPendingJob, jobStatusById],
  )

  return (
    <SafetyReportJobsContext.Provider value={value}>
      {children}
    </SafetyReportJobsContext.Provider>
  )
}

export function useSafetyReportJobs(): SafetyReportJobsContextValue {
  const ctx = useContext(SafetyReportJobsContext)
  if (!ctx) {
    throw new Error(
      'useSafetyReportJobs must be used within SafetyReportJobsProvider',
    )
  }
  return ctx
}
